/**
 * @file DaysList.jsx
 * @module packages/days/src/components/DaysList
 * @description This module defines the DaysList component, which is the primary interface
 * for displaying and managing a list of days within a specific trip. It handles fetching days,
 * creating new days (individually or in bulk), editing day summaries (via EditDayModal),
 * deleting days (with confirmation), and reordering days via drag-and-drop functionality
 * provided by `react-beautiful-dnd` and the `DraggableDaysList` child component.
 *
 * Interacts with:
 * - `react-router-dom` for `useParams` (to get `tripId`) and `useNavigate`.
 * - `@trip-planner-monorepo/shared` for `useAuth` (authentication) and `ConfirmModal`.
 * - `../services/daysService.js` for all backend operations related to days.
 * - Child components: `CreateDays`, `AddDays`, `EditDayModal`, `DraggableDaysList`.
 *
 * The actual rendering of individual, draggable day items is delegated to `DraggableDaysList.jsx`,
 * to which this component passes the fetched `days` array and the current `tripId`.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext } from 'react-beautiful-dnd';
import { useAuth, useTrip, ConfirmModal, resolveDayItineraryContentForTrip } from '@trip-planner-monorepo/shared';
import CreateDays from './CreateDays';
import AddDays from './AddDays';
import EditDayModal from './EditDayModal';
import DraggableDaysList from './DraggableDaysList';
import { FaUndo, FaRedo } from 'react-icons/fa';
import { daysService } from '../services/daysService';
import './DaysList.css';

/**
 * @function DaysList
 * @description A React functional component that manages and displays the list of days for a trip.
 * It provides functionalities for creating, adding, editing (delegated), deleting, and reordering days.
 * It fetches day data using `daysService` based on the `tripId` from URL parameters.
 * @returns {JSX.Element} The rendered list of days with controls for management.
 */
function DaysList() {
  const [days, setDays] = useState([]);
  const [showCreateDays, setShowCreateDays] = useState(true);
  const [showAddDays, setShowAddDays] = useState(false);
  const [editingDay, setEditingDay] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();
  const { effectiveUserId } = useTrip();
  /** @description tripId obtained from URL, used for fetching and managing days specific to this trip. */
  const { tripId } = useParams();
  const navigate = useNavigate();

  // ===== Timezone-safe date helpers (UTC) =====
  const addDaysIsoUtc = (isoDate, offset) => {
    if (!isoDate) return isoDate;
    const [y, m, d] = isoDate.split('-').map(Number);
    const utc = new Date(Date.UTC(y, m - 1, d));
    utc.setUTCDate(utc.getUTCDate() + offset);
    return utc.toISOString().split('T')[0];
  };

  /**
   * @function fetchDays
   * @description Fetches the list of days for the current `tripId` and `currentUser`.
   * Updates the `days` state and determines if the `CreateDays` component should be shown.
   * Wrapped in `useCallback`.
   */
  const fetchDays = useCallback(async () => {
    if (!currentUser || !tripId || !effectiveUserId) return;
    setLoading(true);
    try {
      const fetchedDays = await daysService.fetchDays(effectiveUserId, tripId);
      console.log('Fetched days:', fetchedDays);

      // Resolve itinerary content (stays_on/travel/place registry links merged with legacy
      // blocks) the same way DayDetailPage does, so this list and each day's own detail page
      // never disagree about what's actually on a given day.
      let daysWithResolvedContent = fetchedDays;
      try {
        const resolvedByDayId = await resolveDayItineraryContentForTrip(effectiveUserId, tripId, fetchedDays);
        daysWithResolvedContent = fetchedDays.map((day) => ({
          ...day,
          content: resolvedByDayId.get(day.id)?.content ?? day.content
        }));
      } catch (resolveErr) {
        console.error('Failed to resolve registry itinerary content for days list:', resolveErr);
      }

      setDays(daysWithResolvedContent);
      setShowCreateDays(fetchedDays.length === 0);
    } catch (err) {
      setError('Failed to load days. Please try again.');
      console.error('Error fetching days:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, tripId, effectiveUserId]);

  useEffect(() => {
    fetchDays();
  }, [fetchDays]);

  /**
   * @function handleCreateDays
   * @description Handles the creation of an initial set of days for the trip.
   * Calls `daysService.createMultipleDays` and then re-fetches days.
   * Wrapped in `useCallback`.
   * @param {Array<Object>} newDays - An array of new day objects to be created.
   */
  const handleCreateDays = useCallback(async (newDays) => {
    setLoading(true);
    try {
      await daysService.createMultipleDays(effectiveUserId, tripId, newDays);
      await fetchDays();
      setShowCreateDays(false);
    } catch (err) {
      setError('Failed to create days. Please try again.');
      console.error('Error creating days:', err);
    } finally {
      setLoading(false);
    }
  }, [effectiveUserId, tripId, fetchDays]);

  /**
   * @function handleAddDays
   * @description Handles adding a specified number of new days to the existing list.
   * Calculates new day objects (potentially with sequential dates) and calls `daysService.createMultipleDays`.
   * Wrapped in `useCallback`.
   * @param {string|number} numberOfDaysToAdd - The number of days to add.
   */
  const handleAddDays = useCallback(async (numberOfDaysToAdd) => {
    setLoading(true);
    try {
      const numDays = parseInt(numberOfDaysToAdd, 10);
      if (isNaN(numDays) || numDays < 1 || numDays > 60) {
        throw new Error('Please enter a valid number of days (1-60)');
      }

      const lastDay = days[days.length - 1];
      const lastIso = lastDay && lastDay.date ? lastDay.date : null;

      const newDays = Array.from({ length: numDays }, (_, i) => {
        let date = null;
        if (lastIso) {
          // Start from the day AFTER the last existing date
          date = addDaysIsoUtc(lastIso, i + 1);
        }
        return {
          date,
          order: days.length + i
        };
      });

      await daysService.createMultipleDays(effectiveUserId, tripId, newDays);
      await fetchDays();
      setShowAddDays(false);
    } catch (err) {
      setError(err.message || 'Failed to add days. Please try again.');
      console.error('Error adding days:', err);
    } finally {
      setLoading(false);
    }
  }, [effectiveUserId, tripId, days, fetchDays]);

  /**
   * @function handleSaveEdit
   * @description Callback for when a day's details (e.g., summary from `EditDayModal`) are saved.
   * Updates the local `days` state. The actual persistence is handled by `EditDayModal` or related services.
   * Wrapped in `useCallback`.
   * @param {Array<Object>} updatedDays - The full list of days with the edited day updated.
   */
  const handleSaveEdit = useCallback(async (updatedDays) => {
    console.log('DaysList: handleSaveEdit called with updatedDays:', updatedDays);
    console.log('DaysList: Looking for day with content:', updatedDays.find(d => d.content));
    setDays(updatedDays);
    setEditingDay(null);
  }, []);

  /**
   * @function handleDeleteDay
   * @description Initiates the deletion process for a day by setting up the `confirmDelete` state,
   * which triggers the `ConfirmModal`.
   * Wrapped in `useCallback`.
   * @param {string} dayId - The ID of the day to be deleted.
   */
  const handleDeleteDay = useCallback((dayId) => {
    const dayIndex = days.findIndex(d => d.id === dayId);
    setConfirmDelete({id: dayId, number: dayIndex + 1});
  }, [days]);

  /**
   * @function confirmDeleteDay
   * @description Confirms and executes the deletion of a day after user confirmation.
   * Calls `daysService.deleteDay` and then re-fetches days.
   * Wrapped in `useCallback`.
   */
  const confirmDeleteDay = useCallback(async () => {
    if (confirmDelete) {
      setLoading(true);
      try {
        await daysService.deleteDay(effectiveUserId, tripId, confirmDelete.id);
        await fetchDays();
        setConfirmDelete(null);
      } catch (err) {
        setError('Failed to delete day. Please try again.');
        console.error('Error deleting day:', err);
      } finally {
        setLoading(false);
      }
    }
  }, [confirmDelete, effectiveUserId, tripId, fetchDays]);

  const onDragStart = useCallback((start, provided) => {
    console.log('Drag start:', {
      draggableId: start.draggableId,
      type: start.type,
      source: start.source,
      mode: start.mode,
      days: days.map(d => ({ id: d.id, order: d.order }))
    });
  }, [days]);

  const onDragUpdate = useCallback((update) => {
    console.log('Drag update:', {
      draggableId: update.draggableId,
      type: update.type,
      source: update.source,
      destination: update.destination,
      mode: update.mode
    });
  }, []);

  /**
   * @function onDragEnd
   * @description Callback function triggered when a drag-and-drop operation concludes.
   * Handles the reordering of days if the item was dropped in a new valid position.
   * It recalculates day orders and dates (if applicable, maintaining sequence from the first dated day)
   * and calls `daysService.reorderDays` to persist the changes.
   * Updates local state optimistically and reverts on error by re-fetching.
   * Wrapped in `useCallback`.
   * @param {Object} result - The result object from `react-beautiful-dnd` containing drag source and destination.
   */
  const onDragEnd = useCallback(async (result) => {
    console.log('Drag end:', {
      draggableId: result.draggableId,
      type: result.type,
      source: result.source,
      destination: result.destination,
      reason: result.reason,
      mode: result.mode,
      days: days.map(d => ({ id: d.id, order: d.order }))
    });

    const { destination, source } = result;

    if (!destination) {
      console.log('No destination, dropping operation');
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      console.log('Dropped in same location, ignoring');
      return;
    }

    try {
      const reorderedDays = Array.from(days);
      const [removed] = reorderedDays.splice(source.index, 1);
      reorderedDays.splice(destination.index, 0, removed);

      // Find the first day with a date to use as anchor
      const firstDateIndex = reorderedDays.findIndex(day => day.date);
      if (firstDateIndex !== -1) {
        const firstIso = reorderedDays[firstDateIndex].date; // already ISO YYYY-MM-DD
        
        // Update dates before the first dated day (UTC-safe)
        for (let i = firstDateIndex - 1; i >= 0; i--) {
          reorderedDays[i] = {
            ...reorderedDays[i],
            date: addDaysIsoUtc(firstIso, -(firstDateIndex - i))
          };
        }

        // Update dates after the first dated day (UTC-safe)
        for (let i = firstDateIndex + 1; i < reorderedDays.length; i++) {
          reorderedDays[i] = {
            ...reorderedDays[i],
            date: addDaysIsoUtc(firstIso, (i - firstDateIndex))
          };
        }
      }

      // Update order numbers
      const updatedDays = reorderedDays.map((day, index) => ({
        ...day,
        order: index
      }));

      console.log('Reordering days with updated dates:', {
        source: source,
        destination: destination,
        original: days.map(d => ({ id: d.id, order: d.order, date: d.date })),
        updated: updatedDays.map(d => ({ id: d.id, order: d.order, date: d.date }))
      });

      // Update local state immediately
      setDays(updatedDays);

      // Then update backend
      await daysService.reorderDays(
        effectiveUserId,
        tripId,
        updatedDays
      );

      console.log('Successfully updated day order and dates in backend');
    } catch (err) {
      console.error('Failed to reorder days:', err);
      setError('Failed to reorder days. Please try again.');
      // Revert to original state on error
      await fetchDays();
    }
  }, [effectiveUserId, tripId, days, fetchDays]);

  const toggleAddDays = useCallback(() => {
    setShowAddDays(prev => !prev);
  }, []);

  const openEditModal = useCallback((day) => {
    setEditingDay(day);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditingDay(null);
  }, []);

  return (
    <DragDropContext 
      onDragStart={onDragStart}
      onDragUpdate={onDragUpdate}
      onDragEnd={onDragEnd}
    >
      <div className="days-list-content">
        {error && <p className="error">{error}</p>}
        
        {loading ? (
          <p className="loading">Loading...</p>
        ) : (
          <>
            {days.length === 0 && showCreateDays ? (
              <CreateDays onCreateDays={handleCreateDays} />
            ) : (
              <>
                <div className="controls">
                  <input 
                    type="text" 
                    placeholder="Filter days..." 
                    className="filter-input"
                  />
                  <button className="icon-button">📅</button>
                  <button className="icon-button">✓</button>
                  <button className="add-day-button" onClick={toggleAddDays}>
                    {showAddDays ? 'Cancel' : '+ Add Day(s)'}
                  </button>
                </div>

                {showAddDays && <AddDays onAddDays={handleAddDays} />}

                <div className="undo-redo-controls">
                  <button 
                    onClick={() => {}} 
                    disabled={undoStack.length === 0} 
                    className="icon-button"
                  >
                    <FaUndo /> Undo
                  </button>
                  <button 
                    onClick={() => {}} 
                    disabled={redoStack.length === 0} 
                    className="icon-button"
                  >
                    <FaRedo /> Redo
                  </button>
                </div>

                {days.length > 0 && (
                  <DraggableDaysList
                    days={days}
                    openEditModal={openEditModal}
                    handleDeleteDay={handleDeleteDay}
                    tripId={tripId}
                  />
                )}
              </>
            )}

            <EditDayModal
              isOpen={editingDay !== null}
              onClose={closeEditModal}
              day={editingDay}
              onSave={handleSaveEdit}
              dayNumber={days.findIndex(d => d.id === editingDay?.id) + 1}
              tripId={tripId}
              allDays={days}
            />

            <ConfirmModal
              isOpen={confirmDelete !== null}
              onClose={() => setConfirmDelete(null)}
              onConfirm={confirmDeleteDay}
              dayNumber={confirmDelete?.number}
            />
          </>
        )}
      </div>
    </DragDropContext>
  );
}

export default DaysList;